/**
 * Shared leaf-predicate renderer for a single field's filter condition, covering all
 * 16 canonical operators once so both the trade planner (symbolic style) and the
 * proposal builder (verbose style) render from one source. Style tokens are data
 * (`LeafConditionStyle` presets), the combinator walk and boolean rendering are shared
 * in `describe-filter.ts`, and field labeling stays the caller's.
 *
 * Predicates are emitted in a fixed canonical order (range → equality → membership →
 * filter-list → string ops → null), which is safe only because no real or tested
 * condition puts more than one non-range predicate on a single field.
 */

/** The value types a leaf predicate carries on the wire. */
export type LeafConditionValue = string | number | boolean | null | undefined

/**
 * A flat predicate over one field, with every operator optional. Structurally
 * compatible with `ConstraintFilterCondition` in `constraints.ts` — the renderer
 * only reads fields.
 */
export interface LeafFilterCondition {
  greater_than?: LeafConditionValue
  greater_than_or_equal_to?: LeafConditionValue
  less_than?: LeafConditionValue
  less_than_or_equal_to?: LeafConditionValue
  equal_to?: LeafConditionValue
  not_equal_to?: LeafConditionValue
  in?: readonly LeafConditionValue[]
  not_in?: readonly LeafConditionValue[]
  in_filter_list?: string | null
  not_in_filter_list?: string | null
  is_null?: boolean
  includes?: LeafConditionValue
  not_includes?: LeafConditionValue
  starts_with?: LeafConditionValue
  not_starts_with?: LeafConditionValue
  ends_with?: LeafConditionValue
}

/**
 * Declarative token/format table describing how a style renders each predicate.
 * `lowerBound`/`upperBound` receive whether the bound is inclusive (`≥`/`≤`) or
 * strict (`>`/`<`); verbose collapses both to `min`/`max`.
 */
export interface LeafConditionStyle {
  range: (lower: string, upper: string) => string
  lowerBound: (value: string, inclusive: boolean) => string
  upperBound: (value: string, inclusive: boolean) => string
  equalTo: (value: string) => string
  notEqualTo: (value: string) => string
  inSingle: (value: string) => string
  inMulti: (values: string[]) => string
  notIn: (values: string[]) => string
  inFilterList: (name: string) => string
  notInFilterList: (name: string) => string
  isNull: (value: boolean) => string
  includes: (value: string) => string
  notIncludes: (value: string) => string
  startsWith: (value: string) => string
  notStartsWith: (value: string) => string
  endsWith: (value: string) => string
  /** Separator joining multiple predicates on one field. */
  join: string
}

// The five string ops share one readable phrasing across both presets; only the
// numeric/range/membership/null tokens diverge.
const STRING_OP_STYLE = {
  includes: (value: string) => `includes ${value}`,
  notIncludes: (value: string) => `does not include ${value}`,
  startsWith: (value: string) => `starts with ${value}`,
  notStartsWith: (value: string) => `does not start with ${value}`,
  endsWith: (value: string) => `ends with ${value}`,
} as const

/** Trade-planner tokens: `≥ 1`, `1 to 5`, `= x`, `in [a, b]`, `in list <name>`, `is unknown`. */
export const SYMBOLIC_STYLE: LeafConditionStyle = {
  range: (lower, upper) => `${lower} to ${upper}`,
  lowerBound: (value, inclusive) => `${inclusive ? '≥' : '>'} ${value}`,
  upperBound: (value, inclusive) => `${inclusive ? '≤' : '<'} ${value}`,
  equalTo: (value) => `= ${value}`,
  notEqualTo: (value) => `≠ ${value}`,
  inSingle: (value) => `= ${value}`,
  inMulti: (values) => `in [${values.join(', ')}]`,
  notIn: (values) => `not in [${values.join(', ')}]`,
  inFilterList: (name) => `in list ${name}`,
  notInFilterList: (name) => `not in list ${name}`,
  isNull: (value) => (value ? 'is unknown' : 'is defined'),
  ...STRING_OP_STYLE,
  join: ' and ',
}

/** Proposal-builder tokens: `min 1`, `1-5`, `x`, `in a, b`, `in list <name>`, `must be unknown`. */
export const VERBOSE_STYLE: LeafConditionStyle = {
  range: (lower, upper) => `${lower}-${upper}`,
  lowerBound: (value) => `min ${value}`,
  upperBound: (value) => `max ${value}`,
  equalTo: (value) => value,
  notEqualTo: (value) => `not ${value}`,
  inSingle: (value) => value,
  inMulti: (values) => `in ${values.join(', ')}`,
  notIn: (values) => `not in ${values.join(', ')}`,
  inFilterList: (name) => `in list ${name}`,
  notInFilterList: (name) => `not in list ${name}`,
  isNull: (value) => (value ? 'must be unknown' : 'must be defined'),
  ...STRING_OP_STYLE,
  join: ' and ',
}

/** Rule-row words: `over 20 years`, `1 to 5`, `Connecticut`, `in a, b`, `in list <name>`. */
export const WORDED_STYLE: LeafConditionStyle = {
  range: (lower, upper) => `${lower} to ${upper}`,
  lowerBound: (value, inclusive) =>
    inclusive ? `at least ${value}` : `over ${value}`,
  upperBound: (value, inclusive) =>
    inclusive ? `at most ${value}` : `under ${value}`,
  equalTo: (value) => value,
  notEqualTo: (value) => `not ${value}`,
  inSingle: (value) => value,
  inMulti: (values) => `in ${values.join(', ')}`,
  notIn: (values) => `not in ${values.join(', ')}`,
  inFilterList: (name) => `in list ${name}`,
  notInFilterList: (name) => `not in list ${name}`,
  isNull: SYMBOLIC_STYLE.isNull,
  ...STRING_OP_STYLE,
  join: ' and ',
}

export interface DescribeFilterConditionOptions {
  style: LeafConditionStyle
  /**
   * Per-field value formatter (e.g. rating labels, money). Defaults to `String`.
   * Returning `''` for a bound it cannot render drops that bound as if absent.
   */
  formatValue?: (value: LeafConditionValue) => string
  /** Maps a filter-list id to its human name; falls back to the id when unresolved. */
  resolveListName?: (id: string) => string
}

/**
 * Render a single field's leaf predicate to text, e.g. `≥ 1`, `1 to 5`, `in [a, b]`,
 * `in list High Yield`, `includes GO`. Emits every present predicate in canonical order
 * joined by the style separator; returns `''` when no predicate is present.
 */
export function describeFilterCondition(
  condition: LeafFilterCondition,
  options: DescribeFilterConditionOptions,
): string {
  const { style, formatValue = String, resolveListName } = options
  const parts: string[] = []
  const push = (token: string) => {
    if (token.trim() !== '') parts.push(token)
  }
  const formatList = (values: readonly LeafConditionValue[] | undefined) =>
    (values ?? [])
      .map((value) => (value == null ? '' : formatValue(value)))
      .filter(Boolean)
  const resolve = (id: string) => resolveListName?.(id) ?? id

  // A bound the formatter renders empty counts as absent — the rule `formatList`
  // applies to an `in` member. Without it a value the caller's formatter rejects (a
  // non-numeric bound on a money field) still picks the range or one-sided form and
  // prints a bare `≥ ` or `$NaN`.
  const boundText = (value: LeafConditionValue) => {
    if (value == null) return null
    const text = formatValue(value)
    return text.trim() === '' ? null : text
  }

  // `>=`/`<=` win over the strict variant when both bounds are set.
  const lowerInclusive = boundText(condition.greater_than_or_equal_to)
  const upperInclusive = boundText(condition.less_than_or_equal_to)
  const lower = lowerInclusive ?? boundText(condition.greater_than)
  const upper = upperInclusive ?? boundText(condition.less_than)

  if (lower !== null && upper !== null) {
    push(style.range(lower, upper))
  } else if (lower !== null) {
    push(style.lowerBound(lower, lowerInclusive !== null))
  } else if (upper !== null) {
    push(style.upperBound(upper, upperInclusive !== null))
  }

  if (condition.equal_to != null)
    push(style.equalTo(formatValue(condition.equal_to)))
  if (condition.not_equal_to != null)
    push(style.notEqualTo(formatValue(condition.not_equal_to)))

  // A single-value `in` reads as equality; empty formatted values are dropped.
  const inValues = formatList(condition.in)
  if (inValues.length === 1) push(style.inSingle(inValues[0] ?? ''))
  else if (inValues.length > 1) push(style.inMulti(inValues))
  const notInValues = formatList(condition.not_in)
  if (notInValues.length > 0) push(style.notIn(notInValues))

  if (condition.in_filter_list != null)
    push(style.inFilterList(resolve(String(condition.in_filter_list))))
  if (condition.not_in_filter_list != null)
    push(style.notInFilterList(resolve(String(condition.not_in_filter_list))))

  if (condition.includes != null)
    push(style.includes(formatValue(condition.includes)))
  if (condition.not_includes != null)
    push(style.notIncludes(formatValue(condition.not_includes)))
  if (condition.starts_with != null)
    push(style.startsWith(formatValue(condition.starts_with)))
  if (condition.not_starts_with != null)
    push(style.notStartsWith(formatValue(condition.not_starts_with)))
  if (condition.ends_with != null)
    push(style.endsWith(formatValue(condition.ends_with)))

  if (condition.is_null === true) push(style.isNull(true))
  else if (condition.is_null === false) push(style.isNull(false))

  return parts.join(style.join)
}
